/**
 * Where a click on a notification goes — one resolver for the notifications
 * page, the topbar bell (layouts/DashboardLayout.vue) and push notifications
 * (services/push-notifications.ts).
 *
 * The attendance review and W-2 notification types carry their deep link in
 * `data.url` (in-app) / `url` or `entityUrl` (push data). It is followed only
 * when it is a safe in-app path; otherwise (or when absent) each type has a
 * fallback page built from its own validated ids. Other types keep their
 * existing routing (the resolver answers null).
 */
import { calendarEventPath } from './calendarLink';

export const URL_ROUTED_NOTIFICATION_TYPES: ReadonlySet<string> = new Set([
  'attendance_auto_clockout',
  'attendance_review_pending',
  'attendance_review_resolved',
  'attendance_end_time_submitted',
  'w2_drafts_ready',
  'w2_issued',
]);

type Payload = Record<string, unknown> | null | undefined;

const CONTROL_CHARS = /[\u0000-\u001f\u007f]/;

function currentOrigin(): string | null {
  return typeof window !== 'undefined' && window.location ? window.location.origin : null;
}

/**
 * `raw` as an in-app path ('/x?y=z'), or null: rejects other origins,
 * protocol-relative ('//host') and backslash tricks, schemes such as
 * javascript:, relative paths and control characters. An absolute URL on
 * the app's own origin is reduced to its path.
 */
export function safeInternalPath(raw: unknown, origin: string | null = currentOrigin()): string | null {
  if (typeof raw !== 'string') return null;
  let value = raw.trim();
  if (!value || CONTROL_CHARS.test(value)) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    if (!origin) return null;
    try {
      const url = new URL(value);
      if (url.origin !== origin) return null;
      value = `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return null;
    }
  }
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
}

const positiveInt = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^\d+$/.test(v) ? Number(v) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
};
const year = (v: unknown): number | null => {
  const n = positiveInt(v);
  return n !== null && n >= 1900 && n <= 9999 ? n : null;
};

function fallbackFor(type: string, data: Record<string, unknown>): string {
  const attendanceId = positiveInt(data.attendanceId);
  const taxYear = year(data.taxYear);
  switch (type) {
    case 'attendance_review_pending':
    case 'attendance_end_time_submitted':
      return '/attendance?tab=review';
    case 'attendance_auto_clockout':
    case 'attendance_review_resolved':
      return attendanceId ? `/attendance?tab=records&attendanceId=${attendanceId}` : '/attendance?tab=records';
    case 'w2_drafts_ready':
      return taxYear ? `/payroll?tab=w2&w2Year=${taxYear}` : '/payroll';
    case 'w2_issued':
      return taxYear ? `/payroll?tab=history&w2Year=${taxYear}` : '/payroll?tab=history';
    default:
      return '/notifications';
  }
}

/** The in-app path for a notification of one of the URL-routed types, else null (caller's own routing). */
export function resolveNotificationLink(type: string, payload: Payload): string | null {
  if (!URL_ROUTED_NOTIFICATION_TYPES.has(type)) return null;
  let data: Record<string, unknown> = payload ?? {};
  if (typeof (payload as unknown) === 'string') {
    try {
      data = JSON.parse(payload as unknown as string) ?? {};
    } catch {
      data = {};
    }
  }
  return safeInternalPath(data.url) ?? safeInternalPath(data.entityUrl) ?? fallbackFor(type, data);
}

function parsePayload(payload: Payload | string): Record<string, unknown> {
  if (typeof payload === 'string') {
    try {
      const parsed: unknown = JSON.parse(payload);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return payload ?? {};
}

/** `/base/<id>` or `/base?<param>=<id>` when the id is a positive integer, else the list page. */
function withId(base: string, id: unknown, param?: string): string {
  const n = positiveInt(id);
  if (n === null) return base;
  return param ? `${base}?${param}=${n}` : `${base}/${n}`;
}

/**
 * Where the notifications page sends a click, for every notification type
 * (URL-routed types first, then the per-type pages). Null for unknown types:
 * the click only marks the notification as read. Ids are validated, so a
 * malformed payload falls back to the module's list page.
 */
export function notificationTargetPath(type: string, payload: Payload | string): string | null {
  const data = parsePayload(payload);
  const routed = resolveNotificationLink(type, data);
  if (routed) return routed;
  switch (type) {
    case 'leave_request':
    case 'leave_approved':
    case 'leave_rejected':
      return withId('/leaves', data.leaveId, 'id');
    case 'appreciation':
      return withId('/appreciations', data.appreciationId, 'id');
    case 'task_assigned':
    case 'task_created':
    case 'task_completed':
    case 'task_comment':
    case 'task_status_changed':
      return withId('/tasks', data.taskId);
    case 'project_assigned':
    case 'project_created':
    case 'project_discussion':
    case 'project_status_changed':
      return withId('/projects', data.projectId);
    case 'announcement':
    case 'notice_created':
      return withId('/notices', data.noticeId, 'id');
    case 'message':
    case 'message_received':
    case 'message_mentioned':
      return withId('/messages', data.conversationId);
    case 'ticket':
    case 'ticket_created':
    case 'ticket_assigned':
    case 'ticket_reply':
    case 'ticket_status_changed':
      return withId('/tickets', data.ticketId);
    case 'event_invitation':
    case 'event_updated':
    case 'event_cancelled':
    case 'event_rsvp':
      // The calendar opens the event (there is no /events page).
      return calendarEventPath(data.eventId);
    case 'tdrive_file_shared':
    case 'tdrive_folder_shared': {
      const folderId = positiveInt(data.folderId);
      if (folderId) return `/tdrive?tab=shared-with-me&folder=${folderId}`;
      const fileId = positiveInt(data.fileId);
      if (fileId) return `/tdrive?tab=shared-with-me&file=${fileId}`;
      return '/tdrive?tab=shared-with-me';
    }
    case 'document_shared': {
      const documentId = positiveInt(data.documentId);
      if (documentId && data.documentType) return `/${data.documentType === 'doc' ? 'tdocs' : 'tsheet'}/${documentId}`;
      return '/tdocs';
    }
    case 'asset_assigned':
    case 'asset_returned':
      return '/assets';
    case 'new_lead':
    case 'lead_assigned':
      return withId('/leads', data.leadId, 'leadId');
    default:
      return null;
  }
}
